import React, { useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { PasswordInput } from './PasswordInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Loader2, KeyRound } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { adminApi } from '../utils/api';

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8 || !/[A-Za-z]/.test(newPassword) || !/\d/.test(newPassword)) {
      toast({ title: 'Xəta', description: 'Yeni parol ən azı 8 simvol olmalı, hərf və rəqəm ehtiva etməlidir', variant: 'destructive' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Xəta', description: 'Yeni parol və təkrarı eyni deyil', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.changePassword(currentPassword, newPassword);
      toast({ title: 'Uğurlu', description: 'Parol dəyişdirildi. Növbəti girişdə yeni paroldan istifadə edin.' });
      reset();
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg = 'Parol dəyişdirilə bilmədi';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(d => d.msg || '').filter(Boolean).join('; ') || msg;
      toast({ title: 'Xəta', description: msg, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-blue-700" />
            Parolu dəyiş
          </DialogTitle>
          <DialogDescription>
            Yeni parol ən azı 8 simvol olmalı, hərf və rəqəm ehtiva etməlidir.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="current-password">Hazırkı parol</Label>
            <div className="mt-1.5">
              <PasswordInput
                id="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                autoComplete="off"
                data-testid="current-password-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="new-password">Yeni parol</Label>
            <div className="mt-1.5">
              <PasswordInput
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="new-password-input"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="confirm-password">Yeni parolu təkrar yazın</Label>
            <div className="mt-1.5">
              <PasswordInput
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-blue-700 hover:bg-blue-800 text-white" data-testid="change-password-submit-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Parolu dəyiş
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
